import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Code, Save, ExternalLink } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";

const SiteSettings = ({ adminPassword }) => {
  const [headInject, setHeadInject] = useState("");
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/site-settings`);
      setHeadInject(data.head_inject_homepage || "");
      setOriginal(data.head_inject_homepage || "");
    } catch (e) {
      toast.error("Failed to load site settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, {
        password: adminPassword,
        head_inject_homepage: headInject,
      });
      setOriginal(headInject);
      toast.success("Site settings saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const isDirty = headInject !== original;
  const charCount = headInject.length;

  return (
    <div data-testid="site-settings-panel">
      <div className="mb-6">
        <h2 className="font-serif text-xl font-semibold text-foreground">Site Settings</h2>
        <p className="text-sm text-muted-foreground">
          Code injection for your homepage. Useful for analytics, verification & meta tags.
        </p>
      </div>

      <Card className="bg-white rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="p-2 rounded-full bg-primary/10 flex-shrink-0">
            <Code className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">Homepage — Header Code Injection</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Paste any HTML (scripts, meta tags, verification codes) that should be inserted into
              the <code className="bg-secondary px-1.5 py-0.5 rounded text-xs">&lt;head&gt;</code>{" "}
              of your homepage (<span className="font-medium">/</span>) only.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="head-inject">
              Code to inject{" "}
              <span className="text-xs font-normal text-muted-foreground">(HTML allowed)</span>
            </Label>
            <span className="text-xs text-muted-foreground" data-testid="head-inject-char-count">
              {charCount.toLocaleString()} chars
            </span>
          </div>
          <Textarea
            id="head-inject"
            value={headInject}
            onChange={(e) => setHeadInject(e.target.value)}
            placeholder={
              "<!-- Examples -->\n<!-- Google Search Console verification -->\n<meta name=\"google-site-verification\" content=\"YOUR_CODE\" />\n\n<!-- Google Analytics 4 -->\n<script async src=\"https://www.googletagmanager.com/gtag/js?id=G-XXXXXXX\"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXXXXX');\n</script>"
            }
            rows={18}
            disabled={loading}
            className="rounded-xl font-mono text-xs leading-relaxed"
            data-testid="head-inject-textarea"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-5">
          <Button
            onClick={handleSave}
            disabled={!isDirty || saving || loading}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="save-site-settings-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
          {isDirty && (
            <span className="text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full">
              Unsaved changes
            </span>
          )}
        </div>
      </Card>

      <Card className="bg-secondary/30 border-border/40 rounded-2xl p-5">
        <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
          <ExternalLink className="h-4 w-4 text-primary" /> Common Use Cases
        </h4>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground list-disc list-inside">
          <li>Google Search Console / Bing / Pinterest verification meta tags</li>
          <li>Google Analytics 4 (gtag.js) tracking</li>
          <li>Facebook (Meta) Pixel</li>
          <li>Google Tag Manager container snippet</li>
          <li>Custom <code className="bg-white px-1 py-0.5 rounded text-xs">&lt;meta&gt;</code> tags for SEO/social</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-4 italic">
          Tip: Code is injected only on the homepage. Changes take effect immediately — refresh{" "}
          the homepage in a new tab to verify.
        </p>
      </Card>
    </div>
  );
};

export default SiteSettings;
